import React, { createContext, useContext, useState, useEffect } from "react";
import { ru } from "./ru";
import { en } from "./en";

const I18nContext = createContext(null);

const STORAGE_KEY = "inboost_lang";
export const CIS_COUNTRIES = ["RU", "BY", "KZ", "AM", "KG", "UZ", "TJ"];

export const isMainDomain = (hostname) => {
  const h = (hostname || "").toLowerCase();
  return h === "inboost.pro" || h === "www.inboost.pro";
};

export const isGlobalDomain = (hostname) => {
  const h = (hostname || "").toLowerCase();
  return (
    h.includes("global") ||
    h.endsWith(".pages.dev") ||
    h.includes("pages.dev") ||
    h.includes("workers.dev")
  );
};

export const detectInitialLanguage = () => {
  if (typeof window === "undefined") return "en";

  const pathname = (window.location.pathname || "").toLowerCase();
  const search = window.location.search || "";
  const params = new URLSearchParams(search);
  const hostname = (window.location.hostname || "").toLowerCase();

  // 1. Explicit path prefix
  if (pathname.startsWith("/en")) return "en";
  if (pathname.startsWith("/ru")) return "ru";

  // 2. Explicit query param
  const langParam = params.get("lang");
  if (langParam === "en") return "en";
  if (langParam === "ru") return "ru";

  // 3. Global subdomains and edge hosts ALWAYS default to EN
  if (isGlobalDomain(hostname)) {
    return "en";
  }

  // 4. Session-level forced language (e.g. user selected RU or ?stay=1)
  const sessionForced = sessionStorage.getItem("inboost_force_lang");
  if (sessionForced === "en" || sessionForced === "ru") {
    return sessionForced;
  }

  // 5. GeoIP country cached in sessionStorage
  const cachedCountry = (sessionStorage.getItem("inboost_country") || "").toUpperCase();
  if (cachedCountry) {
    if (CIS_COUNTRIES.includes(cachedCountry)) {
      return "ru";
    }
    return "en";
  }

  // 6. User saved preference from prior manual switches
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "ru") {
    return saved;
  }

  // 7. Browser locale fallback: CIS locales default to RU, all others to EN
  const navLang = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  const ruLocales = ["ru", "be", "kk", "ky", "uz", "tg"];
  if (ruLocales.some((loc) => navLang.startsWith(loc))) {
    return "ru";
  }

  return "en";
};

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLanguage);

  useEffect(() => {
    // Keep document language and title in sync
    document.documentElement.lang = lang;
    if (lang === "en") {
      document.title = "InBoost AI Engine — High-Performance Kubernetes GPU Orchestration";
    } else {
      document.title = "InBoost AI Engine — Катализатор для ИИ-инфраструктуры";
    }
  }, [lang]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hostname = (window.location.hostname || "").toLowerCase();
    const pathname = (window.location.pathname || "").toLowerCase();
    const search = window.location.search || "";
    const params = new URLSearchParams(search);

    // If on global domain, stay on global in EN
    if (isGlobalDomain(hostname)) return;

    // Explicit requests to stay in Russian or skip redirect
    if (
      params.get("lang") === "ru" ||
      pathname.startsWith("/ru") ||
      params.get("noredirect") === "1" ||
      params.get("stay") === "1"
    ) {
      sessionStorage.setItem("inboost_force_lang", "ru");
      return;
    }
    if (sessionStorage.getItem("inboost_force_lang") === "ru") {
      return;
    }

    const redirectToGlobal = () => {
      const target = "https://global.inboost.pro" + window.location.pathname + window.location.search;
      window.location.replace(target);
    };

    const cachedCountry = (sessionStorage.getItem("inboost_country") || "").toUpperCase();
    if (cachedCountry) {
      if (isMainDomain(hostname) && !CIS_COUNTRIES.includes(cachedCountry)) {
        redirectToGlobal();
      } else if (!CIS_COUNTRIES.includes(cachedCountry) && lang !== "en") {
        setLangState("en");
      }
      return;
    }

    // Fast asynchronous GeoIP resolution
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 2500) : null;
    const opts = controller ? { signal: controller.signal } : {};

    const handleCountry = (countryCode) => {
      if (!countryCode) return;
      const code = String(countryCode).toUpperCase();
      sessionStorage.setItem("inboost_country", code);

      if (!CIS_COUNTRIES.includes(code)) {
        // Foreign IP (e.g., VPN or international visitor)
        if (isMainDomain(hostname)) {
          redirectToGlobal();
        } else {
          setLangState("en");
        }
      } else {
        // Russian / CIS IP
        if (lang !== "ru" && !params.get("lang") && !pathname.startsWith("/en")) {
          setLangState("ru");
        }
      }
    };

    fetch("https://get.geojs.io/v1/ip/country.json", opts)
      .then((res) => res.json())
      .then((data) => {
        if (timeoutId) clearTimeout(timeoutId);
        handleCountry(data && data.country);
      })
      .catch(() => {
        return fetch("https://api.country.is/", opts)
          .then((res) => res.json())
          .then((data) => {
            if (timeoutId) clearTimeout(timeoutId);
            handleCountry(data && data.country);
          });
      })
      .catch(() => {
        // Silently ignore if offline or blocked
      });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [lang]);

  const setLang = (nextLang) => {
    const valid = nextLang === "en" ? "en" : "ru";
    setLangState(valid);
    try {
      localStorage.setItem(STORAGE_KEY, valid);
      sessionStorage.setItem("inboost_force_lang", valid);
    } catch (e) {
      // ignore storage write errors
    }
  };

  const isEn = lang === "en";
  const t = isEn ? en : ru;

  return (
    <I18nContext.Provider value={{ lang, setLang, isEn, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return ctx;
};
